/**
 * WHAT:
 *   Custom hook for managing all TestTab component state.
 *
 * WHY:
 *   Centralizes state management for better organization and reusability.
 *
 * DOMAIN: Investments → Parqet → Test Tab → Hooks
 *
 * DEPENDENCIES:
 *   - React useState hook
 *   - validateParsedData utility function
 *   - TypeScript types (TestTabState, ScanResults, etc.)
 *
 * RISKS:
 *   - NOTE: Large state object may impact performance
 *   - NOTE: State setters are passed down - ensure proper memoization in consumers
 */

import type {
  TestTabState,
  ScanResults,
  ValidationStatus,
  YahooFinanceResult,
  SecuritiesDataEntry,
  ErrorPdf,
  PdfData,
  RawDataDebug,
  ParsedData,
} from "../types";

import { useState } from "react";

import { validateParsedData as validateParsedDataUtility } from "../utils";

/**
 * WHAT:
 *   Custom hook that manages all state for the TestTab component.
 *
 * WHY:
 *   Centralizes all useState calls in one place for better organization.
 *
 * OUTPUT:
 *   TestTabState object containing all state values and setters.
 *
 * SIDE EFFECTS:
 *   None - Pure hook, only manages local state
 *
 * RISKS:
 *   - NOTE: Returns large object with many properties - consider splitting if performance issues occur
 *   - NOTE: All state is initialized - ensure proper reset logic in consumers
 *
 * @returns {TestTabState} Object containing all state values and setters
 *
 */
export const useTestTabState = (): TestTabState => {
  // File upload state
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | undefined>();
  const [fileInputKey, setFileInputKey] = useState(0);

  // Parsing state
  const [parsedData, setParsedData] = useState<null | ParsedData>(null);
  const [rawPdfText, setRawPdfText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<null | string>(null);

  // PDF navigation state
  const [parsedPdfs, setParsedPdfs] = useState<PdfData[]>([]);
  const [failedPdfs, setFailedPdfs] = useState<PdfData[]>([]);
  const [currentPdfIndex, setCurrentPdfIndex] = useState(0);
  const [browseFailedOnly, setBrowseFailedOnly] = useState(false);
  const [scanResults, setScanResults] = useState<null | ScanResults>(null);

  // Raw data debug state
  const [rawData, setRawData] = useState<null | RawDataDebug>(null);
  const [showRawData, setShowRawData] = useState(false);

  // Validation state
  const [validationStatus, setValidationStatus] = useState<ValidationStatus>({
    canImport: false,
    errors: [],
    isValid: false,
  });

  // Scan progress state
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanTotal, setScanTotal] = useState(0);
  const [currentScanFile, setCurrentScanFile] = useState("");
  const [scanElapsedMs, setScanElapsedMs] = useState(0);
  const [scanPps, setScanPps] = useState(0);
  const [scanAvgMsPerPdf, setScanAvgMsPerPdf] = useState(0);
  const [scanStartTs, setScanStartTs] = useState<null | number>(null);
  const [skipEnrichment, setSkipEnrichment] = useState<boolean>(false);

  // Yahoo Finance test state
  const [yahooIdentifier, setYahooIdentifier] = useState("");
  const [yahooResult, setYahooResult] = useState<null | YahooFinanceResult>(null);
  const [yahooLoading, setYahooLoading] = useState(false);

  // Securities data state
  const [securitiesDataMap, setSecuritiesDataMap] = useState<Record<string, SecuritiesDataEntry>>(
    {},
  );
  const [isLoadingSecurities, setIsLoadingSecurities] = useState(false);

  // Legacy error state
  const [allErrorPdfs, setAllErrorPdfs] = useState<ErrorPdf[]>([]);
  const [currentErrorIndex, setCurrentErrorIndex] = useState(0);

  return {
    // Legacy error state
    allErrorPdfs,
    browseFailedOnly,
    currentErrorIndex,
    currentPdfIndex,
    currentScanFile,
    error,

    failedPdfs,
    fileInputKey,
    // File upload state
    isDragOver,
    isLoadingSecurities,
    isProcessing,
    // Scan progress state
    isScanning,
    // Parsing state
    parsedData,
    // PDF navigation state
    parsedPdfs,

    // Raw data debug state
    rawData,
    rawPdfText,
    scanAvgMsPerPdf,
    scanElapsedMs,
    scanPps,
    scanProgress,
    scanResults,
    scanStartTs,
    scanTotal,
    // Securities data state
    securitiesDataMap,

    setAllErrorPdfs,
    setBrowseFailedOnly,
    setCurrentErrorIndex,
    setCurrentPdfIndex,

    setCurrentScanFile,
    setError: ((error: null | string) => {
      setError(error);
    }) as (error: null | string) => void,

    setFailedPdfs,
    setFileInputKey,
    setIsDragOver,
    setIsLoadingSecurities,
    setIsProcessing,
    setIsScanning,
    setParsedData,
    setParsedPdfs,
    setRawData: ((data: null | RawDataDebug) => {
      setRawData(data);
    }) as (data: null | RawDataDebug) => void,
    setRawPdfText,
    setScanAvgMsPerPdf,
    setScanElapsedMs,
    setScanPps,
    setScanProgress,
    setScanResults: ((results: null | ScanResults) => {
      setScanResults(results);
    }) as (results: null | ScanResults) => void,
    setScanStartTs: ((value: null | number) => {
      setScanStartTs(value);
    }) as (value: null | number) => void,
    setScanTotal,
    setSecuritiesDataMap,

    setShowRawData,
    setSkipEnrichment,
    setUploadedFile: ((file: File | null) => {
      setUploadedFile(file ?? undefined);
    }) as (file: File | null) => void,
    setValidationStatus,
    setYahooIdentifier,
    setYahooLoading,

    setYahooResult,
    showRawData,
    skipEnrichment,
    uploadedFile: uploadedFile ?? null,

    // Validation function
    validateParsedData: validateParsedDataUtility,
    // Validation state
    validationStatus,
    // Yahoo Finance test state
    yahooIdentifier,
    yahooLoading,

    yahooResult,
  };
};
